import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { PageHeader, Card, CardHeader, StatusPill, PriorityPill, EmptyState, AtomBadge } from "../components/primitives";
import { formatNGN } from "../lib/format";
import { Search, X, ExternalLink, ChevronDown, ChevronRight, SlidersHorizontal } from "lucide-react";

const STATUS_FILTERS = ["","Open","In review","Awaiting MLRO review","MLRO reviewing","Recommend SAR","SAR drafted","SAR filed","Closed - cleared"];
const PRIORITY_FILTERS = ["","P1","P2","P3","P4"];

export default function Cases() {
  const [cases, setCases] = useState([]);
  const [filter, setFilter] = useState({ status: "", priority: "", typology: "", q: "" });
  const [selected, setSelected] = useState(null);
  const [expandedRow, setExpandedRow] = useState(null);
  const [sortKey, setSortKey] = useState("opened");
  const [sortDir, setSortDir] = useState("desc");

  useEffect(() => { api.get("/cases").then(r => setCases(r.data.cases || [])); }, []);

  const typologies = useMemo(() => Array.from(new Set(cases.map(c => c.typology))).sort(), [cases]);

  const filtered = useMemo(() => {
    let rows = cases.filter(c =>
      (!filter.status || c.status === filter.status) &&
      (!filter.priority || c.priority === filter.priority) &&
      (!filter.typology || c.typology === filter.typology) &&
      (!filter.q || `${c.id} ${c.subject} ${c.owner} ${c.typology}`.toLowerCase().includes(filter.q.toLowerCase()))
    );
    rows = [...rows].sort((a, b) => {
      const va = a[sortKey] || ""; const vb = b[sortKey] || "";
      const cmp = typeof va === "number" ? va - vb : String(va).localeCompare(String(vb));
      return sortDir === "asc" ? cmp : -cmp;
    });
    return rows;
  }, [cases, filter, sortKey, sortDir]);

  const stats = useMemo(() => ({
    total: filtered.length,
    p1: filtered.filter(c => c.priority === "P1").length,
    pendingChecker: filtered.filter(c => c.pending_checker).length,
    totalAmount: filtered.reduce((s, c) => s + (c.amount || 0), 0),
    sarFiled: filtered.filter(c => c.status === "SAR filed").length,
  }), [filtered]);

  const toggleSort = (k) => {
    if (sortKey === k) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else { setSortKey(k); setSortDir("desc"); }
  };

  const sortArrow = (k) => sortKey === k ? (sortDir === "asc" ? " ↑" : " ↓") : "";

  return (
    <div>
      <PageHeader
        title="Case Management"
        subtitle={`${stats.total} cases · ${stats.p1} P1 · ${stats.pendingChecker} pending checker · ${formatNGN(stats.totalAmount, { short: true })} at risk`}
        sections={["5.7"]}
      />

      {/* Filter bar */}
      <div className="px-6 py-3 bg-bg-surface border-b border-line space-y-2">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-1 px-2 py-1 border border-line rounded-lg bg-white flex-1 max-w-[360px]">
            <Search size={13} className="text-ink-subtle" strokeWidth={1.5} />
            <input data-testid="cases-search" value={filter.q} onChange={e => setFilter({ ...filter, q: e.target.value })} placeholder="Search case ID, subject, owner, typology…" className="bg-transparent text-[13px] outline-none flex-1" />
          </div>
          <div className="flex items-center gap-1">
            <SlidersHorizontal size={12} className="text-ink-subtle mr-1" strokeWidth={1.5} />
            <span className="text-[11px] text-ink-subtle">Priority:</span>
            {PRIORITY_FILTERS.map(p => (
              <button key={p} onClick={() => setFilter({ ...filter, priority: p })} className={`px-2 py-0.5 text-[11px] rounded-full border ${filter.priority === p ? 'bg-navy text-white border-navy' : 'border-line hover:bg-bg-hover'}`}>{p || "All"}</button>
            ))}
          </div>
          <div className="flex items-center gap-1">
            <span className="text-[11px] text-ink-subtle">Typology:</span>
            <select value={filter.typology} onChange={e => setFilter({ ...filter, typology: e.target.value })} className="px-2 py-0.5 text-[11px] border border-line rounded-full bg-white">
              <option value="">All</option>
              {typologies.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          {(filter.q || filter.priority || filter.typology || filter.status) && (
            <button onClick={() => setFilter({ status: "", priority: "", typology: "", q: "" })} className="px-2 py-0.5 text-[11px] text-ink-subtle hover:text-ink">Clear filters</button>
          )}
        </div>
        <div className="flex items-center gap-1 flex-wrap">
          <span className="text-[11px] text-ink-subtle mr-1">Status:</span>
          {STATUS_FILTERS.map(s => (
            <button key={s} onClick={() => setFilter({ ...filter, status: s })} className={`px-2 py-0.5 text-[11px] rounded-full border ${filter.status === s ? 'bg-navy text-white border-navy' : 'border-line hover:bg-bg-hover'}`}>{s || "All"}</button>
          ))}
        </div>
      </div>

      <div className={`p-6 grid gap-3 ${selected ? "grid-cols-[minmax(0,1fr)_340px]" : "grid-cols-1"}`}>
        <Card className="min-w-0">
          <div className="overflow-x-auto">
            <table className="dense w-full tabular">
              <thead><tr>
                <th className="w-6"></th>
                <th onClick={() => toggleSort("id")} className="cursor-pointer hover:text-ink">Case ID{sortArrow("id")}</th>
                <th>Subject</th>
                <th onClick={() => toggleSort("priority")} className="cursor-pointer hover:text-ink">Priority{sortArrow("priority")}</th>
                <th onClick={() => toggleSort("status")} className="cursor-pointer hover:text-ink">Status{sortArrow("status")}</th>
                <th>Owner</th>
                <th>MLRO</th>
                <th onClick={() => toggleSort("typology")} className="cursor-pointer hover:text-ink">Typology{sortArrow("typology")}</th>
                <th onClick={() => toggleSort("amount")} className="cursor-pointer hover:text-ink text-right">Amount{sortArrow("amount")}</th>
                <th>Opened</th>
                <th>Maker-checker</th>
              </tr></thead>
              <tbody>
                {filtered.map(c => (
                  <>
                    <tr key={c.id} data-testid={`case-row-${c.id}`} onClick={() => setSelected(c)} className={`cursor-pointer ${selected?.id === c.id ? "bg-amber/5" : ""}`}>
                      <td onClick={(e) => { e.stopPropagation(); setExpandedRow(expandedRow === c.id ? null : c.id); }}>
                        {expandedRow === c.id ? <ChevronDown size={13} strokeWidth={1.5} /> : <ChevronRight size={13} strokeWidth={1.5} className="text-ink-subtle hover:text-ink" />}
                      </td>
                      <td><span className="text-amber-600 hover:underline font-medium">{c.id}</span></td>
                      <td className="text-[12px]"><span className="font-medium">{c.subject}</span><div className="text-[10px] text-ink-subtle">{c.subject_cid}</div></td>
                      <td><PriorityPill priority={c.priority} /></td>
                      <td><StatusPill status={c.status} /></td>
                      <td className="text-[12px]">{c.owner}</td>
                      <td className="text-[12px]">{c.mlro}</td>
                      <td className="text-[11px] text-ink-subtle">{c.typology}</td>
                      <td className="text-right tabular">{formatNGN(c.amount, { short: true })}</td>
                      <td className="text-[11px] text-ink-subtle">{c.opened}</td>
                      <td className="text-[11px]">{c.pending_checker ? <span className="text-risk-medium">Pending {c.checker}</span> : c.checker ? <span className="text-risk-low">Approved · {c.checker}</span> : <span className="text-ink-subtle">—</span>}</td>
                    </tr>
                    {expandedRow === c.id && (
                      <tr className="bg-bg-warm">
                        <td colSpan={11} className="p-0">
                          <div className="p-4 grid grid-cols-3 gap-4 border-t-2 border-amber/30">
                            <div>
                              <div className="text-[10px] uppercase tracking-wider text-ink-subtle mb-1.5">Implicated transactions (sample)</div>
                              {Array.from({ length: 4 }).map((_, i) => (
                                <div key={i} className="flex items-center justify-between py-1 border-b border-line/40 last:border-0">
                                  <div>
                                    <div className="text-[11px] hash-mono">TXN2026051{4 - i}…</div>
                                    <div className="text-[10px] text-ink-subtle">{14 - i} May 2026</div>
                                  </div>
                                  <div className="tabular text-[11px] font-medium">{formatNGN(c.amount / 6, { short: true })}</div>
                                </div>
                              ))}
                            </div>
                            <div>
                              <div className="text-[10px] uppercase tracking-wider text-ink-subtle mb-1.5">Recent activity</div>
                              <div className="space-y-1 text-[11px]">
                                <div><span className="text-ink-subtle">11:42 WAT</span> · {c.owner} submitted for MLRO review</div>
                                <div><span className="text-ink-subtle">12:08 WAT</span> · {c.mlro} confirmed STR drafting</div>
                                <div><span className="text-ink-subtle">14:33 WAT</span> · Status set to <span className="font-medium">{c.status}</span></div>
                                <div><span className="text-ink-subtle">15:11 WAT</span> · Atom Nucleus narrative ratified</div>
                              </div>
                            </div>
                            <div>
                              <div className="text-[10px] uppercase tracking-wider text-ink-subtle mb-1.5">Quick actions</div>
                              <div className="flex flex-col gap-1.5">
                                <Link to={`/cases/${c.id}`} className="px-2.5 py-1.5 text-[11px] bg-navy text-white rounded-md text-center hover:bg-navy-700">Open full case →</Link>
                                <Link to={`/cases/${c.id}#network`} className="px-2.5 py-1.5 text-[11px] border border-line rounded-md text-center hover:bg-bg-hover">View network analysis</Link>
                                <Link to={`/customers/${c.subject_cid}`} className="px-2.5 py-1.5 text-[11px] border border-line rounded-md text-center hover:bg-bg-hover">Open customer 360</Link>
                                <button className="px-2.5 py-1.5 text-[11px] border border-amber/40 bg-amber/8 text-amber-600 rounded-md hover:bg-amber/15">Draft STR</button>
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                ))}
              </tbody>
            </table>
          </div>
          {filtered.length === 0 && <EmptyState />}
          <div className="px-3 py-2 border-t border-line flex items-center justify-between text-[11px] text-ink-subtle">
            <div>Showing {filtered.length} of {cases.length} · {stats.sarFiled} SARs filed</div>
            <div className="flex items-center gap-2">
              <button className="px-2 py-0.5 border border-line rounded-md hover:bg-bg-hover">Export CSV</button>
              <button className="px-2 py-0.5 border border-line rounded-md hover:bg-bg-hover">Export PDF</button>
            </div>
          </div>
        </Card>

        {/* Side panel preview */}
        {selected && (
          <Card>
            <div className="px-4 py-3 border-b border-line bg-gradient-to-r from-amber/8 to-white flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <PriorityPill priority={selected.priority} />
                  <StatusPill status={selected.status} />
                </div>
                <div className="display text-[15px] font-semibold tracking-tight">{selected.subject}</div>
                <div className="text-[11px] text-ink-subtle">{selected.id} · {selected.typology}</div>
              </div>
              <button onClick={() => setSelected(null)} className="p-1 hover:bg-bg-hover rounded-md"><X size={14} strokeWidth={1.5} /></button>
            </div>
            <div className="p-3 text-[12px] space-y-1.5">
              <div className="flex justify-between"><span className="text-ink-subtle">Owner</span><span>{selected.owner}</span></div>
              <div className="flex justify-between"><span className="text-ink-subtle">MLRO</span><span>{selected.mlro}</span></div>
              <div className="flex justify-between"><span className="text-ink-subtle">Originating alert</span><Link to={`/alerts/${selected.alert}`} className="text-amber-600 hover:underline">{selected.alert}</Link></div>
              <div className="flex justify-between"><span className="text-ink-subtle">Customer</span><Link to={`/customers/${selected.subject_cid}`} className="text-amber-600 hover:underline">{selected.subject_cid}</Link></div>
              <div className="flex justify-between"><span className="text-ink-subtle">Opened</span><span>{selected.opened}</span></div>
              <div className="flex justify-between"><span className="text-ink-subtle">CBN</span><span>{(selected.cbn || []).join(", ")}</span></div>
              <div className="flex justify-between pt-2 border-t border-line"><span className="text-ink-subtle">Amount at risk</span><span className="tabular font-medium">{formatNGN(selected.amount)}</span></div>
              {selected.pending_checker && (
                <div className="mt-2 p-2 rounded-md bg-risk-medium/10 border border-risk-medium/30 text-[11px]">
                  <div className="font-medium text-risk-medium">Awaiting maker-checker</div>
                  <div className="text-ink-muted">Approval needed from {selected.checker}.</div>
                  <div className="flex gap-1.5 mt-2">
                    <button data-testid={`checker-approve-${selected.id}`} onClick={async () => {
                      try { const r = await api.post(`/cases/${selected.id}/checker`, { action: "approve" }); setCases(prev => prev.map(c => c.id === selected.id ? r.data.case : c)); setSelected(r.data.case); } catch(e) {}
                    }} className="px-2 py-1 text-[10px] bg-risk-low text-white rounded font-medium hover:opacity-90">Approve</button>
                    <button data-testid={`checker-reject-${selected.id}`} onClick={async () => {
                      const reason = window.prompt("Reject reason?", "Need additional source-of-funds documentation");
                      if (!reason) return;
                      try { const r = await api.post(`/cases/${selected.id}/checker`, { action: "reject", reason }); setCases(prev => prev.map(c => c.id === selected.id ? r.data.case : c)); setSelected(r.data.case); } catch(e) {}
                    }} className="px-2 py-1 text-[10px] border border-line rounded hover:bg-bg-hover">Reject</button>
                    <button onClick={async () => {
                      const reason = window.prompt("Info requested?", "Confirm BO declaration");
                      if (!reason) return;
                      try { const r = await api.post(`/cases/${selected.id}/checker`, { action: "request_info", reason }); setCases(prev => prev.map(c => c.id === selected.id ? r.data.case : c)); setSelected(r.data.case); } catch(e) {}
                    }} className="px-2 py-1 text-[10px] border border-line rounded hover:bg-bg-hover">Request info</button>
                  </div>
                </div>
              )}
              {selected.checker_action && (
                <div className="mt-2 p-2 rounded-md bg-bg-warm border border-line text-[10px] text-ink-muted">
                  {selected.checker_action}
                </div>
              )}
            </div>
            <div className="px-3 pb-3 space-y-1.5">
              <Link to={`/cases/${selected.id}`} className="flex items-center justify-center gap-1.5 px-3 py-2 text-[12px] bg-navy text-white rounded-lg hover:bg-navy-700">
                Open full case <ExternalLink size={12} strokeWidth={1.5} />
              </Link>
              <div className="grid grid-cols-2 gap-1.5">
                <button className="px-2 py-1.5 text-[11px] border border-line rounded-md hover:bg-bg-hover">Reassign</button>
                <button className="px-2 py-1.5 text-[11px] border border-line rounded-md hover:bg-bg-hover">Add note</button>
              </div>
            </div>
            <div className="px-3 pb-3 pt-2 border-t border-line">
              <div className="text-[10px] uppercase tracking-wider text-ink-subtle mb-1">Atom Nucleus suggested</div>
              <div className="text-[11px] text-ink-muted">Recommend MLRO review → STR draft within 24h. Confidence 0.91.</div>
              <div className="mt-2"><AtomBadge model="Atom Nucleus" version="v3.5" /></div>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
